// E8FractionOfRouteClosedTask.js
// Zadanie zamkniete E8 (2025 CZE zad. 7) - ulamek danej liczby (cala trasa z czesci).
// Przejechano d km = (num/den) trasy => cala = d*den/num; pytamy o polowe trasy.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildData({ traveled, num, den, fixedOptions = null }) {
  const route = (traveled * den) / num;
  const half = route / 2;

  let options;
  let correctIndex;
  if (fixedOptions) {
    options = fixedOptions.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = fixedOptions.indexOf(half);
  } else {
    const pool = [half - 3, half - 1, half, half + 1, half + 2, traveled / 2];
    const unique = [];
    pool.forEach((v) => {
      if (v > 0 && Number.isInteger(v) && !unique.includes(v)) unique.push(v);
    });
    const distractors = unique.filter((v) => v !== half).slice(0, 3);
    const ordered = shuffle([half, ...distractors]);
    options = ordered.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = ordered.indexOf(half);
  }

  return { traveled, num, den, route, half, options, correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8FractionOfRouteClosedTask",
    data: buildData({ traveled: 12, num: 2, den: 3, fixedOptions: [6, 8, 9, 10] }),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 7 },
    tags: ["e8", "ulamki", "ulamek-liczby"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  let traveled;
  let num;
  let den;
  let route;
  do {
    [num, den] = pick([[2, 3], [3, 4], [3, 5], [2, 5], [4, 5], [5, 6]]);
    const k = pick([2, 3, 4, 5, 6]);
    traveled = num * k; // gwarantuje cala = den*k, polowa calkowita gdy den*k parzyste
    route = (traveled * den) / num;
  } while (!Number.isInteger(route / 2) || route / 2 > 60);

  return {
    type: "E8FractionOfRouteClosedTask",
    data: buildData({ traveled, num, den }),
    source: { variant: "random", basedOn: "E8_2025_CZE_7" },
    tags: ["e8", "ulamki", "ulamek-liczby"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Jurek przejechał rowerem <InlineMath math={`${data.traveled}\\ \\text{km}`} />, co
        stanowiło <InlineMath math={`\\frac{${data.num}}{${data.den}}`} /> długości
        zaplanowanej trasy.
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>Połowa zaplanowanej trasy ma długość</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.value}>
            <ChoiceOptionMath letter={LETTERS[index]} math={`${option.value}\\ \\text{km}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({ label: LETTERS[index], textNode: LETTERS[index] }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={`${data.half}\\ \\text{km}`} />;
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${option.value}\\ \\text{km}`} />;
}

export function renderExplanation({ data }) {
  const { traveled, num, den, route, half } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Skoro <InlineMath math={`${traveled}\\ \\text{km}`} /> to <InlineMath math={`\\frac{${num}}{${den}}`} /> trasy, to cała trasa:</p>
      <BlockMath math={`${traveled}:\\frac{${num}}{${den}}=${traveled}\\cdot\\frac{${den}}{${num}}=${route}\\ \\text{km}`} />
      <p><strong>Krok 2.</strong> Połowa trasy:</p>
      <BlockMath math={`\\frac{${route}}{2}=${half}\\ \\text{km}`} />
      <p>Poprawna odpowiedź to <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.</p>
    </div>
  );
}
